<template>
  <div class="payment-callback">
    <div class="pc-card">
      <!-- ── Success state ── -->
      <template v-if="status === 'success'">
        <div class="pc-icon-wrap pc-icon-wrap--success">
          <svg class="pc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h1 class="pc-title">{{ $t('PaymentSuccessful') }}</h1>
        <p class="pc-desc">{{ $t('PaymentSuccessDesc') }}</p>
      </template>

      <!-- ── Error state ── -->
      <template v-else-if="status === 'error' || status === 'cancelled'">
        <div class="pc-icon-wrap pc-icon-wrap--error">
          <svg class="pc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h1 class="pc-title">{{ status === 'cancelled' ? $t('PaymentCancelled') : $t('PaymentFailed') }}</h1>
        <p class="pc-desc">{{ status === 'cancelled' ? $t('PaymentCancelledDesc') : $t('PaymentFailedDesc') }}</p>
      </template>

      <!-- ── Unknown / loading ── -->
      <template v-else>
        <div class="pc-icon-wrap pc-icon-wrap--info">
          <svg class="pc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </div>
        <h1 class="pc-title">{{ $t('ProcessingPayment') }}</h1>
        <p class="pc-desc">{{ $t('ProcessingPaymentDesc') }}</p>
      </template>

      <!-- ── Transaction details ── -->
      <div v-if="txnId" class="pc-txn">
        <span class="pc-txn-label">{{ $t('TransactionID') }}</span>
        <span class="pc-txn-value">{{ txnId }}</span>
      </div>

      <!-- ── Deep link + fallback ── -->
      <div class="pc-actions">
        <el-button
          v-if="status === 'success'"
          type="primary"
          size="large"
          class="pc-btn"
          @click="openAppDeepLink"
        >
          {{ $t('ReturnToApp') }}
        </el-button>

        <el-button
          v-if="status === 'success' && deepLinkFailed"
          type="default"
          size="large"
          class="pc-btn"
          @click="retryDeepLink"
        >
          {{ $t('TryAgain') }}
        </el-button>

        <el-button
          v-if="status !== 'success'"
          type="primary"
          size="large"
          class="pc-btn"
          @click="goToCheckout"
        >
          {{ $t('TryAgain') }}
        </el-button>
      </div>

      <!-- ── App download fallback for new users ── -->
      <div class="pc-download-fallback">
        <p class="pc-download-text">{{ $t('DontHaveApp') }}</p>
        <a
          :href="appStoreUrl"
          class="pc-download-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t('DownloadApp') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { useAppleDevice } from '@/composables/useAppleDevice'
import {
  buildDeepLink,
  openDeepLink,
  getPendingTransaction,
  clearPendingTransaction,
} from '@/utils/mobilePayment'
import { useSEO } from '@/composables/useSEO'

export default {
  name: 'PaymentCallback',
  setup() {
    const { isAppleDevice, appStoreUrl } = useAppleDevice()
    return { isAppleDevice, appStoreUrl }
  },
  data() {
    return {
      status: 'loading', // success | error | cancelled | loading
      txnId: null,
      deepLinkFailed: false,
    }
  },
  mounted() {
    document.documentElement.classList.add('mc-scroll')
    document.body.classList.add('mc-scroll')

    useSEO({
      title: 'Payment — WordCloudStudio',
      description: 'Payment processing for WordCloudStudio subscription.',
      canonical: '/payment-callback',
      noindex: true,
    })

    this.parseQueryParams()
    // Auto-attempt deep link if success
    if (this.status === 'success') {
      this.openAppDeepLink()
    }
  },
  beforeUnmount() {
    document.documentElement.classList.remove('mc-scroll')
    document.body.classList.remove('mc-scroll')
  },
  methods: {
    parseQueryParams() {
      const params = new URLSearchParams(window.location.search)
      this.status = params.get('status') || 'loading'
      this.txnId = params.get('txn') || params.get('transaction_id') || null

      // Also try to get txn from sessionStorage stash
      if (!this.txnId) {
        const stashed = getPendingTransaction()
        if (stashed) {
          this.txnId = stashed.transactionId
        }
      }
    },

    async openAppDeepLink() {
      const txn = this.txnId
      const deepLinkUrl = buildDeepLink('payment-success', { txn })

      const opened = await openDeepLink(deepLinkUrl, 3000)
      if (!opened) {
        this.deepLinkFailed = true
      } else {
        clearPendingTransaction()
      }
    },

    retryDeepLink() {
      this.deepLinkFailed = false
      this.openAppDeepLink()
    },

    goToCheckout() {
      this.$router.push({ name: 'mobile-checkout', query: this.$route.query })
    },
  },
}
</script>

<style scoped>
.payment-callback {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-lg);
  background: var(--color-surface-100, #f3f4f6);
}

.pc-card {
  background: var(--color-surface-0);
  border-radius: var(--radius-xl);
  padding: var(--space-2xl);
  text-align: center;
  max-width: 24rem;
  width: 100%;
  box-shadow: var(--shadow-lg);
  animation: pc-fadeIn 0.4s ease;
}

@keyframes pc-fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.pc-icon-wrap {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-lg);
}

.pc-icon-wrap--success {
  background: rgba(34, 197, 94, 0.1);
  color: var(--color-success);
}

.pc-icon-wrap--error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error, #ef4444);
}

.pc-icon-wrap--info {
  background: rgba(59, 130, 246, 0.1);
  color: var(--color-info, #3b82f6);
}

.pc-icon {
  width: 2rem;
  height: 2rem;
}

.pc-title {
  font-size: var(--text-heading);
  font-weight: 700;
  color: var(--color-text-900);
  margin: 0 0 var(--space-xs);
}

.pc-desc {
  font-size: var(--text-body);
  color: var(--color-text-400);
  margin: 0 0 var(--space-lg);
  line-height: var(--leading-relaxed);
}

.pc-txn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3xs);
  padding: var(--space-sm);
  background: var(--color-surface-50);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-lg);
}

.pc-txn-label {
  font-size: var(--text-overline);
  color: var(--color-text-400);
  text-transform: uppercase;
  letter-spacing: var(--tracking-overline);
}

.pc-txn-value {
  font-size: var(--text-caption);
  font-family: monospace;
  color: var(--color-text-600);
  word-break: break-all;
}

.pc-actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.pc-btn {
  width: 100%;
  border-radius: var(--radius-md) !important;
  height: 2.75rem !important;
  font-weight: 600 !important;
}

.pc-download-fallback {
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-surface-200);
}

.pc-download-text {
  font-size: var(--text-caption);
  color: var(--color-text-400);
  margin: 0 0 var(--space-xs);
}

.pc-download-link {
  font-size: var(--text-caption);
  color: var(--color-brand-600);
  font-weight: 600;
  text-decoration: none;
}
</style>
